import type React from "react";

import { CircleHelpIcon } from "lucide-react";

import { Button, type ButtonProps } from "src/components/Button";
import { Popover } from "src/components/Popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/primitives/Tooltip";
import { renderIcon, type IconProp } from "src/shared/renderIcon";
import { cn } from "src/shadcn/lib/utils";

const SIDE_MAP: Record<string, "top" | "right" | "bottom" | "left"> = {
  top: "top",
  bottom: "bottom",
  left: "left",
  right: "right",
  auto: "top",
};

export interface HelpIconTooltipProps {
  /** Tooltip content. */
  content?: React.ReactNode;
  /** Tooltip position. */
  position?: string;
}

export interface HelpIconPopoverProps {
  /** Popover title. */
  title?: React.ReactNode;
  /** Popover description. */
  description?: React.ReactNode;
  /** Props for an optional help link button inside the popover. */
  helpLinkProps?: ButtonProps;
}

export interface HelpIconProps {
  /** Click handler on the help icon. */
  onClick?: () => void;
  /** Custom icon. Defaults to CircleHelpIcon. */
  icon?: IconProp;
  /** Show a tooltip on hover. */
  tooltipProps?: HelpIconTooltipProps;
  /** Show a popover on hover with title, description, and optional link. */
  popoverProps?: HelpIconPopoverProps;
  /** Additional class name for the help icon. */
  className?: string;
}

export const HelpIcon = (helpIconProps: HelpIconProps) => {
  const icon = helpIconProps.icon ?? CircleHelpIcon;

  const iconElement = (
    <span
      className={cn(
        "inline-flex cursor-help text-muted-foreground transition-colors hover:text-foreground",
        helpIconProps.onClick && "cursor-pointer",
        helpIconProps.className
      )}
      onClick={helpIconProps.onClick}
    >
      {renderIcon(icon, "size-3.5")}
    </span>
  );

  if (helpIconProps.popoverProps) {
    const { title, description, helpLinkProps } = helpIconProps.popoverProps;

    return (
      <Popover trigger={iconElement}>
        {title && <Popover.Title>{title}</Popover.Title>}
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
        {helpLinkProps && (
          <Button
            variant="link"
            size="sm"
            className="mt-1 h-auto self-start p-0"
            {...helpLinkProps}
            href={helpLinkProps.href}
          />
        )}
      </Popover>
    );
  }

  if (helpIconProps.tooltipProps?.content) {
    const side =
      SIDE_MAP[helpIconProps.tooltipProps.position ?? "auto"] ?? "top";

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{iconElement}</TooltipTrigger>
          <TooltipContent side={side}>
            {helpIconProps.tooltipProps.content}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return iconElement;
};
